import type { ChatMessage } from '@skychat/shared-core';

export interface MessageSyncV2ManifestItem {
  seq: number;
  id?: string;
  version?: number;
  edited?: boolean;
  deleted?: boolean;
  reactionCount?: number;
}

export interface MessageSyncV2ManifestResponse {
  ok: boolean;
  format?: string;
  watermark?: number;
  sequences?: number[];
  mutable?: MessageSyncV2ManifestItem[];
  legacy?: MessageSyncV2ManifestItem[];
  items?: MessageSyncV2ManifestItem[];
  serverTime?: number;
  error?: string;
  unsupported?: boolean;
  httpStatus?: number;
}

export interface MessageSyncV2Request {
  cursor: number;
  limit?: number;
  knownInsertSeqs?: number[];
  manifestBootstrap?: boolean;
  bootstrapBaselineCursor?: number;
  bootstrapFetchAll?: boolean;
  bootstrapFetchSeqs?: number[];
  bootstrapFetchIds?: string[];
}

export interface MessageSyncV2Response {
  ok: boolean;
  messages?: Array<ChatMessage & { syncSeq?: number; syncKind?: string }>;
  nextCursor?: number;
  watermark?: number;
  hasMore?: boolean;
  cursorReset?: boolean;
  bootstrapComplete?: boolean;
  serverTime?: number;
  error?: string;
  unsupported?: boolean;
  httpStatus?: number;
}

function normalizedBaseUrl(baseUrl: string): string {
  return String(baseUrl || '').replace(/\/+$/, '');
}

async function postJson<T extends { ok: boolean; error?: string; unsupported?: boolean; httpStatus?: number }>(
  baseUrl: string,
  token: string,
  path: string,
  payload: Record<string, unknown>
): Promise<T> {
  const response = await fetch(`${normalizedBaseUrl(baseUrl)}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  let data: T;
  try {
    data = await response.json() as T;
  } catch {
    data = { ok: false, error: `Invalid response (${response.status})` } as T;
  }

  data.httpStatus = response.status;
  if (response.status === 404 || response.status === 405) data.unsupported = true;
  return data;
}

export function requestMessageSyncV2Manifest(
  baseUrl: string,
  token: string
): Promise<MessageSyncV2ManifestResponse> {
  return postJson<MessageSyncV2ManifestResponse>(
    baseUrl,
    token,
    '/api/messages/sync-v2/manifest',
    { format: 'compact2' }
  );
}

export function requestMessageSyncV2(
  baseUrl: string,
  token: string,
  payload: MessageSyncV2Request
): Promise<MessageSyncV2Response> {
  return postJson<MessageSyncV2Response>(baseUrl, token, '/api/messages/sync-v2', payload as unknown as Record<string, unknown>);
}
